/**
 * Global API Error Handler with Toast Notifications
 * Provides consistent error handling across the application
 */

import { ErrorTypes, classifyError, getErrorInfo } from './ErrorSystem';
import logger from '../utils/logger';

// Toast notification system (uses native browser API as fallback)
let toastContainer = null;

const createToastContainer = () => {
  if (toastContainer) return toastContainer;
  
  toastContainer = document.createElement('div');
  toastContainer.id = 'global-toast-container';
  toastContainer.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 400px;
  `;
  document.body.appendChild(toastContainer);
  return toastContainer;
};

const TOAST_TYPES = {
  success: {
    bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    icon: '✓',
    title: 'Success'
  },
  error: {
    bg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    icon: '✕',
    title: 'Error'
  },
  warning: {
    bg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    icon: '⚠',
    title: 'Warning'
  },
  info: {
    bg: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    icon: 'ℹ',
    title: 'Info'
  }
};

// Demo mode flag - set to true to hide error toasts during demos
const DEMO_MODE = true;

/**
 * Show a toast notification
 */
export const showToast = (message, type = 'info', duration = 5000, options = {}) => {
  // In demo mode, suppress error toasts
  if (DEMO_MODE && type === 'error') {
    return;
  }
  
  const container = createToastContainer();
  const config = TOAST_TYPES[type] || TOAST_TYPES.info;
  const { action, actionLabel, onAction, title } = options;
  
  const toast = document.createElement('div');
  toast.style.cssText = `
    background: ${config.bg};
    color: white;
    padding: 14px 18px;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    animation: slideIn 0.3s ease;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 100%;
    word-wrap: break-word;
    backdrop-filter: blur(8px);
  `;
  
  let actionHtml = '';
  if (action || (actionLabel && onAction)) {
    actionHtml = `
      <div style="margin-top: 8px; display: flex; gap: 8px;">
        ${actionLabel ? `<button id="toast-action-btn" style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 4px 10px; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: 500;">${actionLabel}</button>` : ''}
        ${action ? `<span style="opacity: 0.8; font-size: 12px;">${action}</span>` : ''}
      </div>
    `;
  }
  
  toast.innerHTML = `
    <span style="font-size: 18px; line-height: 1; flex-shrink: 0;">${config.icon}</span>
    <div style="flex: 1; min-width: 0;">
      <div style="font-weight: 600; margin-bottom: 4px;">${title || config.title}</div>
      <div style="opacity: 0.95; line-height: 1.4;">${message}</div>
      ${actionHtml}
    </div>
    <button style="background: none; border: none; color: white; cursor: pointer; padding: 0; font-size: 20px; opacity: 0.7; line-height: 1; flex-shrink: 0; transition: opacity 0.2s;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'" onclick="this.parentElement.remove()">×</button>
  `;
  
  // Add animation keyframes if not exists
  if (!document.getElementById('toast-animations')) {
    const style = document.createElement('style');
    style.id = 'toast-animations';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }
  
  container.appendChild(toast);
  
  // Add action button click handler
  if (onAction) {
    const actionBtn = toast.querySelector('#toast-action-btn');
    if (actionBtn) {
      actionBtn.onclick = () => {
        onAction();
        toast.remove();
      };
    }
  }
  
  // Auto remove
  setTimeout(() => {
    toast.style.animation = 'slideOut 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, duration);
  
  return toast;
};

/**
 * Common error messages mapping
 */
const ERROR_MESSAGES = {
  // Network errors
  'Failed to fetch': 'Unable to connect to server. Please check your internet connection.',
  'NetworkError': 'Network error. Please check your connection and try again.',
  'Load failed': 'Failed to load data. Please refresh the page.',
  
  // Auth errors
  'Unauthorized': 'Your session has expired. Please log in again.',
  'Invalid credentials': 'Invalid email or password.',
  'Token expired': 'Your session has expired. Please log in again.',
  'Access denied': 'You do not have permission to perform this action.',
  
  // Validation errors
  'Validation error': 'Please check your input and try again.',
  'Required field': 'Please fill in all required fields.',
  
  // Server errors
  'Internal server error': 'Something went wrong on our end. Please try again.',
  'Service unavailable': 'Service temporarily unavailable. Please try again later.',
  'Rate limit exceeded': 'Too many requests. Please wait a moment and try again.',
  
  // AI errors
  'AI service temporarily unavailable': 'AI service is busy. Please try again in a moment.',
  'Request timed out': 'The request took too long. Please try again.',
};

/**
 * Parse backend error response
 */
export const parseBackendError = (error) => {
  // Check if it's our new structured error format
  if (error?.error && typeof error.error === 'object') {
    return {
      type: error.error.type || ErrorTypes.UNKNOWN,
      message: error.error.message || 'An error occurred',
      detail: error.error.detail,
      action: error.error.action,
      retryable: error.error.retryable !== false
    };
  }
  
  // Legacy format - just a detail string
  if (error?.detail && typeof error.detail === 'string') {
    return {
      type: classifyError({ message: error.detail }),
      message: error.detail,
      retryable: true
    };
  }
  
  // Error object with message
  if (error?.message) {
    return {
      type: classifyError(error),
      message: error.message,
      retryable: true
    };
  }
  
  return {
    type: ErrorTypes.UNKNOWN,
    message: 'An unexpected error occurred',
    retryable: true
  };
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error) => {
  // First try to parse as backend error
  const parsed = parseBackendError(error);
  if (parsed.message && parsed.message !== 'An error occurred') {
    return parsed.message;
  }
  
  // String error
  if (typeof error === 'string') {
    // Check for known error patterns
    for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.toLowerCase().includes(pattern.toLowerCase())) {
        return message;
      }
    }
    return error;
  }
  
  if (error instanceof Error) {
    return getErrorMessage(error.message);
  }
  
  if (error?.detail) {
    if (typeof error.detail === 'object') {
      return error.detail.message || error.detail.detail || 'An error occurred';
    }
    return getErrorMessage(error.detail);
  }
  
  if (error?.message) {
    return getErrorMessage(error.message);
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Handle API errors with appropriate user feedback
 */
export const handleApiError = (error, context = '', options = {}) => {
  logger.error(`[API Error]${context ? ` ${context}:` : ''}`, error);
  
  const parsed = parseBackendError(error);
  const message = parsed.message || getErrorMessage(error);
  
  // Determine toast type based on error type
  let toastType = 'error';
  if (parsed.type === ErrorTypes.AUTH || parsed.type === ErrorTypes.RATE_LIMIT) {
    toastType = 'warning';
  }
  
  // Check for auth errors - need to redirect
  if (parsed.type === ErrorTypes.AUTH || message.includes('session') || message.includes('log in')) {
    showToast(message, 'warning', 5000, {
      action: parsed.action,
      actionLabel: 'Log In',
      onAction: () => { window.location.href = '/login'; }
    });
    return;
  }
  
  // Show toast with action hint if available
  showToast(message, toastType, 6000, {
    action: parsed.action,
    ...options
  });
  
  return parsed;
};

/**
 * Enhanced fetch wrapper with automatic error handling
 */
export const safeFetch = async (url, options = {}, showErrors = true) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { detail: `HTTP ${response.status}` };
      }
      
      // Handle specific status codes
      if (response.status === 401) {
        if (showErrors) {
          showToast('Your session has expired. Please log in again.', 'warning', 5000, {
            actionLabel: 'Log In',
            onAction: () => { window.location.href = '/login'; }
          });
        }
        localStorage.removeItem('asp_token');
        localStorage.removeItem('asp_user');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
        return null;
      }
      
      if (response.status === 403) {
        if (showErrors) showToast('You do not have permission to perform this action.', 'error');
        return null;
      }
      
      if (response.status === 404) {
        if (showErrors) showToast('The requested resource was not found.', 'error');
        return null;
      }
      
      if (response.status === 429) {
        const retryAfter = errorData?.error?.metadata?.retry_after || 60;
        if (showErrors) showToast(`Too many requests. Please wait ${retryAfter} seconds.`, 'warning');
        return null;
      }
      
      if (response.status >= 500) {
        if (showErrors) {
          const parsed = parseBackendError(errorData);
          showToast(parsed.message || 'Server error. Please try again later.', 'error', 6000, {
            action: parsed.action
          });
        }
        return null;
      }
      
      throw new Error(errorData?.error?.message || errorData.detail || `Request failed with status ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    if (showErrors) {
      handleApiError(error);
    }
    throw error;
  }
};

/**
 * Get auth headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('asp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Success toast shorthand
 */
export const showSuccess = (message, options = {}) => showToast(message, 'success', 4000, options);

/**
 * Error toast shorthand
 */
export const showError = (message, options = {}) => showToast(message, 'error', 6000, options);

/**
 * Warning toast shorthand
 */
export const showWarning = (message, options = {}) => showToast(message, 'warning', 5000, options);

/**
 * Info toast shorthand
 */
export const showInfo = (message, options = {}) => showToast(message, 'info', 4000, options);

export default {
  showToast,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  handleApiError,
  getErrorMessage,
  parseBackendError,
  safeFetch,
};
